/**
 * Screener question config.
 *
 * The screener is the applicant's first interaction with Greenbroad.
 * Before they invest an hour in the full ~556-field intake, this
 * 22-question pre-qualification flow filters out clearly ineligible
 * filings in 5–10 minutes.
 *
 * Per the Phase 3 plan ("Screener subset (proposed)" section), each
 * descriptor binds to a real catalog `FieldId` whenever one exists;
 * the build-time test in `screenerConfig.catalog.test.ts` asserts
 * those IDs resolve. Where the plan proposes a question that the
 * Phase 0 catalog has not yet grounded (e.g., the `eligibility.
 * unlawful_presence.*` 9C bar cluster, or the `application.basis.
 * for_filing` path-qualifier), the descriptor carries
 * `catalogFieldId: null` and the answer is held in screener-internal
 * storage only — those answers do NOT cross-write into the intake's
 * `AnswerStore`. A Phase 6 follow-up should expand the catalog to
 * cover those qualifiers; the screener gracefully picks them up
 * once they exist.
 *
 * The ordering reflects the Phase 3 plan's decision tree:
 *   1. Path qualifiers — refer-different-form short-circuits early
 *   2. Sponsor qualifiers — refer-different-form / refer-attorney
 *   3. Hard disqualifiers — ineligible_*
 *   4. Strong disqualifiers — refer_attorney
 *   5. Path-different qualifiers — refer_different_form
 *   6. Severe-personal-harm consolidation — ineligible_criminal
 *
 * The router (`screenerRouter.ts`) walks this list in order, asks
 * each question, and short-circuits on the first answer that maps
 * to a terminal outcome.
 */
import type { FieldId } from '@inside-us-aos/domain';

/**
 * The five terminal outcomes the screener can route to. The router
 * computes this from a partial answer set; the UI renders one of
 * five outcome pages per ID.
 */
export type ScreenerOutcomeId =
  | 'eligible'
  | 'ineligible_criminal'
  | 'ineligible_removal'
  | 'refer_attorney'
  | 'refer_different_form';

/**
 * Question kinds the screener UI knows how to render. Kept narrower
 * than the catalog's full `valueType` set because the screener is a
 * polished landing flow, not the spreadsheet-y intake — only inputs
 * that fit a single-question-per-screen layout are allowed.
 */
export type ScreenerQuestionKind =
  | 'boolean'         // Yes/No radio
  | 'single_select'   // Segmented buttons / radio list
  | 'multi_select';   // "Select all that apply" checkbox group

/** A choice in a `single_select` or `multi_select` question. */
export interface IScreenerOption {
  readonly value: string;
  readonly label: string;
  /** Optional sub-line beneath the option label. */
  readonly description?: string;
}

/**
 * One step in the screener flow. Reference-stable: the router and UI
 * key off `id`, never the array index, so reorderings don't change
 * resume behavior.
 */
export interface IScreenerQuestion {
  /** Stable URL-safe identifier (used in `/screener/q/:questionId`). */
  readonly id: string;
  /**
   * Catalog field this question contributes to, or `null` when the
   * question is screener-internal (no catalog grounding yet — Phase 6
   * may add one). Cross-write into the intake `AnswerStore` only
   * fires for non-null entries.
   */
  readonly catalogFieldId: FieldId | null;
  /** Question prompt as the user reads it. Conversational tone. */
  readonly displayCopy: string;
  /** Optional short helper line beneath the prompt. */
  readonly helpText?: string;
  /** Render kind. Drives the input the question card shows. */
  readonly kind: ScreenerQuestionKind;
  /** Options for `single_select` / `multi_select`; ignored for `boolean`. */
  readonly options?: readonly IScreenerOption[];
  /**
   * Display order. Identical to the array index — duplicated here so a
   * caller can resort by `order` without depending on iteration order.
   */
  readonly order: number;
  /**
   * Optional preview blurb shown on the landing page when listing the
   * topics covered. Keeps the landing page honest about scope.
   */
  readonly topicHint?: string;
}

/**
 * Path-qualifier choices for question 1 (filing path). The screener
 * only continues when the user is filing a marriage-based AOS as the
 * USC-spouse beneficiary; everything else routes to
 * `refer_different_form`. (The plan's
 * `application.basis.for_filing` field is not yet in the catalog;
 * this mirrors that intent.)
 */
// Option helpers rewritten in plain
// English. The previous "Form I-485 with an I-130 spouse petition"
// subtitle was USCIS jargon for an applicant landing on the screener
// for the first time. New subtitles describe the path conversationally
// and only mention form numbers in parens when they help.
const FILING_PATH_OPTIONS: readonly IScreenerOption[] = [
  {
    value: 'spouse_of_us_citizen',
    label: 'I am applying through marriage to a U.S. citizen',
    description: 'The standard marriage-based green card path — what we help with.',
  },
  {
    value: 'spouse_of_lpr',
    label: 'I am applying through marriage to a lawful permanent resident',
    description:
      "We don't currently support this path. We'll point you to resources.",
  },
  {
    value: 'employment',
    label: 'I am applying through employment',
    description: 'Employment-based (the form your employer files is the I-140).',
  },
  {
    value: 'asylee_or_refugee',
    label: 'I have asylee or refugee status',
    description: 'Different track — we are not the right fit, and we will say why.',
  },
  {
    value: 'family_other',
    label:
      'I am applying through a different family relationship',
    description: 'Parent, child, sibling, or other family member sponsoring you.',
  },
  {
    value: 'other',
    label: 'Something else, or I am not sure',
    description: "We will help you figure out which path fits and where to go from here.",
  },
];

const INSIDE_US_OPTIONS: readonly IScreenerOption[] = [
  { value: 'inside', label: 'Yes, I am physically inside the U.S.' },
  { value: 'outside', label: 'No, I am outside the U.S.' },
];

const SPONSOR_CITIZENSHIP_OPTIONS: readonly IScreenerOption[] = [
  { value: 'us_citizen', label: 'U.S. citizen' },
  { value: 'lpr', label: 'Lawful permanent resident (green-card holder)' },
  { value: 'unsure', label: "I'm not sure" },
];

// Sponsor income options shortened.
// The "125% of the federal poverty guidelines" framing is technically
// accurate but reads as financial-aid-form jargon. Reframed around
// what the applicant can confidently know without doing math.
const SPONSOR_INCOME_OPTIONS: readonly IScreenerOption[] = [
  {
    value: 'at_or_above',
    label: "Yes — sponsor's income clears the USCIS threshold",
    description: "USCIS sets a household-size threshold (125% of the federal poverty line). Most full-time U.S. workers clear it.",
  },
  {
    value: 'below',
    label: "No — sponsor's income is below the threshold",
    description:
      "A joint sponsor or substantial assets may still qualify. We will walk you through what to do.",
  },
  {
    value: 'unsure',
    label: "I'm not sure",
    description: "That's fine — we will help work it out together.",
  },
];

/**
 * Severe-personal-harm bundle (Phase 3 plan's "soft-no / encourage-
 * attorney consult" consolidation, Slice 11 catalog rows). Backed by
 * `eligibility.security.killed_any_person`,
 * `eligibility.security.severely_injured_any_person`,
 * `eligibility.security.committed_torture_or_genocide`,
 * `eligibility.security.committed_terrorist_activity`. Cross-write
 * fans the multi-select back into the four catalog booleans.
 */
const SEVERE_HARM_OPTIONS: readonly IScreenerOption[] = [
  {
    value: 'killed',
    label: 'Caused the death of another person, intentionally or unintentionally',
  },
  {
    value: 'severely_injured',
    label: 'Severely injured another person, intentionally or unintentionally',
  },
  {
    value: 'torture_or_genocide',
    label: 'Participated in torture, genocide, or extrajudicial killings',
  },
  {
    value: 'terrorist_activity',
    label:
      'Took part in any of: hijacking, sabotage, kidnapping, political assassination, or use of explosives against people or property',
  },
  {
    value: 'none',
    label: 'None of the above',
  },
];

/**
 * Bar-question consolidation. Five individual
 * Yes/No questions about prior U.S. immigration interactions collapse
 * into a single "check any that apply" page. Routing logic in
 * `screenerRouter.ts` checks each value separately so the existing
 * outcomes (`ineligible_removal` for hard removal orders, `refer_attorney`
 * for soft items) still fire.
 */
const IMMIGRATION_HISTORY_OPTIONS: readonly IScreenerOption[] = [
  {
    value: 'final_removal_order',
    label:
      "I've been issued a final order of exclusion, deportation, or removal",
  },
  {
    value: 'reinstated_removal_order',
    label:
      "I've had a prior order of exclusion, deportation, or removal reinstated against me",
  },
  {
    value: 'removal_proceedings',
    label:
      "I'm currently in, or have been in, removal, exclusion, rescission, or deportation proceedings",
  },
  {
    value: 'unlawful_presence_over_one_year',
    label:
      'I was unlawfully present in the U.S. for more than one year, then departed and re-entered (the "9C" bar)',
  },
  {
    value: 'misrepresentation',
    label:
      "I've lied or misrepresented myself on a U.S. immigration application or to a U.S. immigration officer (including falsely claiming U.S. citizenship)",
  },
  {
    value: 'none',
    label: 'None of these apply to me',
  },
];

/**
 * Companion to `IMMIGRATION_HISTORY_OPTIONS`. Six criminal
 * / public-charge bar items collapse into a single multi-select page.
 * The terrorist-activity option from the legacy
 * `security_committed_terrorist_activity` boolean is intentionally
 * NOT mirrored here — it's already covered by the severe-personal-harm
 * bundle below, which fans out to the same catalog field.
 */
const CRIMINAL_PUBLIC_CHARGE_OPTIONS: readonly IScreenerOption[] = [
  {
    value: 'drug_trafficking',
    label:
      "I've illicitly trafficked a controlled substance, or knowingly benefited from someone who did",
  },
  {
    value: 'human_trafficking',
    label:
      "I've trafficked persons for involuntary servitude, peonage, debt bondage, or slavery",
  },
  {
    value: 'terrorist_group_member',
    label:
      "I've been a member of a group or organization that has engaged in terrorist activity",
  },
  {
    value: 'arrested_or_detained',
    label:
      "I've been arrested, cited, charged, or detained by any law-enforcement officer (anywhere in the world, including minor citations)",
  },
  {
    value: 'convicted',
    label:
      "I've pleaded guilty to, or been convicted of, an offense (anywhere in the world)",
  },
  {
    value: 'public_charge',
    label:
      "I've received certain public benefits the U.S. counts toward the public-charge ground (cash welfare, Medicaid for long-term institutional care)",
  },
  {
    value: 'none',
    label: 'None of these apply to me',
  },
];

/**
 * The screener's 22-step config. Every entry that has a non-null
 * `catalogFieldId` references a verified catalog field — the
 * accompanying test fails the build if a string typo'd id ever
 * sneaks in.
 */
export const screenerQuestions: readonly IScreenerQuestion[] = [
  // ── Path qualifiers (1–4) ────────────────────────────────────────
  {
    id: 'filing_path',
    catalogFieldId: null,
    displayCopy: "Which path are you applying through?",
    helpText:
      "We help with marriage-based green cards (Form I-485). Other paths follow different rules.",
    kind: 'single_select',
    options: FILING_PATH_OPTIONS,
    order: 1,
    topicHint: 'Your filing path',
  },
  {
    id: 'inside_united_states',
    catalogFieldId: null,
    displayCopy: 'Are you physically inside the United States right now?',
    helpText:
      'Adjustment of Status (Form I-485) is for people already in the U.S. If you are outside, you would use consular processing through a U.S. embassy.',
    kind: 'single_select',
    options: INSIDE_US_OPTIONS,
    order: 2,
    topicHint: 'Where you are filing from',
  },
  {
    id: 'beneficiary_immigration_status',
    catalogFieldId: 'beneficiary.immigration_status.current' as FieldId,
    displayCopy: 'How did you most recently enter or stay in the U.S.?',
    helpText: "We'll use this to confirm you're on a path that supports adjustment.",
    kind: 'single_select',
    options: [
      { value: 'lawful_entry_visa', label: 'I entered lawfully on a visa (e.g., F-1, H-1B, B-1/B-2, K-1)' },
      { value: 'lawful_entry_vwp', label: 'I entered through the Visa Waiver Program (ESTA)' },
      { value: 'parolee', label: 'I was paroled into the U.S.' },
      { value: 'asylee_or_refugee', label: 'I hold asylee or refugee status' },
      { value: 'tps', label: 'I hold Temporary Protected Status (TPS)' },
      { value: 'entered_without_inspection', label: 'I entered without inspection' },
      { value: 'other', label: 'Something else' },
    ],
    order: 3,
    topicHint: 'Your current status',
  },
  {
    id: 'derivative_or_primary',
    catalogFieldId: null,
    displayCopy:
      'Are you the primary applicant on this case, or a derivative (e.g., spouse or child of a primary applicant)?',
    kind: 'single_select',
    options: [
      { value: 'primary', label: 'I am the primary applicant' },
      { value: 'derivative', label: 'I am a derivative applicant' },
    ],
    order: 4,
    topicHint: 'Primary vs. derivative',
  },

  // ── Sponsor qualifiers (5–7) ─────────────────────────────────────
  {
    id: 'sponsor_citizenship',
    catalogFieldId: 'sponsor.citizenship.status' as FieldId,
    displayCopy: 'Is your sponsoring spouse a U.S. citizen or a lawful permanent resident?',
    helpText: 'For our v1 we support U.S.-citizen sponsors; LPR sponsors follow a different path.',
    kind: 'single_select',
    options: SPONSOR_CITIZENSHIP_OPTIONS,
    order: 5,
    topicHint: 'Sponsor citizenship',
  },
  {
    id: 'sponsor_income_bracket',
    catalogFieldId: null,
    displayCopy:
      'Is your sponsor confident their household income is at or above 125% of the federal poverty guidelines for their household size?',
    helpText:
      'This is the financial-support requirement on Form I-864. A "no" does not stop your case — many couples use a joint sponsor.',
    kind: 'single_select',
    options: SPONSOR_INCOME_OPTIONS,
    order: 6,
    topicHint: 'Sponsor financial support',
  },
  {
    id: 'has_marriage_certificate',
    catalogFieldId: null,
    displayCopy: 'Do you and your spouse have a government-issued marriage certificate?',
    helpText:
      "We'll ask for a copy later. If your marriage certificate is not yet finalized, that is OK — finish the marriage first, then come back.",
    kind: 'boolean',
    order: 7,
    topicHint: 'Proof of marriage',
  },

  // ── Immigration-history consolidation (8) ────────────────────────
  // Five legacy booleans
  // (removal_final_order, removal_reinstated, removal_in_proceedings,
  // unlawful_presence_over_one_year, immigration_misrepresentation)
  // collapsed into one multi-select. Router checks each value for the
  // matching outcome (hard ineligible_removal for orders, soft
  // refer_attorney for the rest).
  {
    id: 'immigration_history_bundle',
    catalogFieldId: null,
    displayCopy: "Have any of these ever applied to you?",
    helpText:
      'These are the prior-immigration-interaction questions every AOS packet has to answer. Check anything that applies — if nothing does, pick "None of these apply to me" and move on.',
    kind: 'multi_select',
    options: IMMIGRATION_HISTORY_OPTIONS,
    order: 8,
    topicHint: 'Immigration history',
  },

  // ── Criminal & public-charge consolidation (9) ────────────────────
  // Six legacy booleans
  // (criminal_drug_trafficking, criminal_human_trafficking,
  // security_terrorist_membership, criminal_arrested,
  // criminal_convicted, public_charge_subject) collapsed into one
  // multi-select. `security_committed_terrorist_activity` is NOT
  // re-asked here because the severe-harm bundle below (option
  // `terrorist_activity`) already covers it via the handoff fan-out.
  {
    id: 'criminal_public_charge_bundle',
    catalogFieldId: null,
    displayCopy: 'Have any of these ever applied to you?',
    helpText:
      'These are the criminal and public-charge screening items. Check anything that applies — even minor citations belong here so the case-team can review.',
    kind: 'multi_select',
    options: CRIMINAL_PUBLIC_CHARGE_OPTIONS,
    order: 9,
    topicHint: 'Criminal & public-charge history',
  },

  // ── Path-different qualifiers (10–11) ────────────────────────────
  {
    id: 'j1_two_year_rule',
    catalogFieldId: null,
    displayCopy:
      'Were you ever a J-1 exchange visitor subject to the two-year home-residence requirement?',
    helpText:
      'If yes, the two-year rule must be satisfied or waived before adjustment.',
    kind: 'boolean',
    order: 10,
    topicHint: 'J-1 two-year rule',
  },
  {
    id: 'j1_two_year_complied',
    catalogFieldId:
      'eligibility.j_exchange_visitor.complied_with_two_year_rule' as FieldId,
    displayCopy:
      'Have you completed the two-year home-residence requirement, or received a waiver?',
    helpText: 'Only relevant if you answered yes to the previous question.',
    kind: 'single_select',
    options: [
      { value: 'completed', label: 'Yes, I completed the two years' },
      { value: 'waiver', label: 'Yes, I received a waiver or favorable recommendation' },
      { value: 'no', label: 'No, neither' },
      { value: 'na', label: 'Not applicable — I was never on a J-1' },
    ],
    order: 11,
    topicHint: 'J-1 two-year rule',
  },

  // ── Severe-personal-harm consolidation (12) ──────────────────────
  {
    id: 'security_severe_harm_bundle',
    catalogFieldId: null,
    displayCopy: 'Have any of these ever applied to you?',
    helpText:
      "Anyone applying for a green card has to confirm they haven't been part of these activities. We ask once; the intake will round-trip the specific answers later.",
    kind: 'multi_select',
    options: SEVERE_HARM_OPTIONS,
    order: 12,
    topicHint: 'Severe-personal-harm screen',
  },
] as const;

/**
 * Quick lookup map (id → question). Stable iteration order matches
 * the array.
 */
export const screenerQuestionsById: ReadonlyMap<string, IScreenerQuestion> =
  new Map(screenerQuestions.map(q => [q.id, q]));

/** Total number of questions in the screener flow. */
export const screenerQuestionCount: number = screenerQuestions.length;
